import { __awaiter } from "tslib";
import { callFandomService, callFandomServiceWithBody, callFandomServiceWithFormData } from 'pathfinder/utils/api';
import log from 'pathfinder/utils/log';
import { Product } from '@fandom/context';
import { isDevDomain } from 'utils/env';
import { isLocalHost } from 'fandom-frontend-ready/env';
export function fetchQuizDataFromQuizId(quizId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!quizId) {
            return;
        }
        const response = yield callFandomService(`quizzes/quizzes/${quizId}`);
        if (!response.ok || response.status === 404) {
            log.debug(`The quizzes API did not return any consumable data. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            return null;
        }
        return (yield response.json());
    });
}
export function fetchSuggestedQuizData(siteId, quizId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!siteId) {
            return;
        }
        let response;
        if (quizId) {
            response = yield callFandomService(`quizzes/suggested?siteId=${siteId}&currentQuizId=${quizId}`);
        }
        else {
            response = yield callFandomService(`quizzes/suggested?siteId=${siteId}`);
        }
        if (!response.ok || response.status === 404) {
            log.debug(`The quizzes API did not return any consumable data. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            return null;
        }
        return (yield response.json());
    });
}
export function validateQuizAnswersFromQuizId(quizId, userAnswers) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!quizId) {
            return;
        }
        const answers = userAnswers.map((answer) => {
            return {
                questionId: answer.questionId,
                answerId: answer.answerId,
            };
        });
        const response = yield callFandomService(`quizzes/quizzes/${quizId}/validate_answers`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ answers }),
        });
        if (!response.ok) {
            log.error(`The quizzes API did not return any user score. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            return null;
        }
        return (yield response.json());
    });
}
export function reportQuizQuestion(questionId, reasonDescription, reasonCode) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield callFandomService('quizzes/genai/questions/report', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify([{ questionId, reasonDescription, reasonCode }]),
        });
        if (!response.ok) {
            log.error(`The quizzes API error occurred during reporting a question. Response.ok: ${response.ok} | Response.status: ${response.status}`);
        }
    });
}
export function getRecentLeaderboardDetails(quizId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!quizId) {
            return;
        }
        const response = yield callFandomService(`quizzes/leaderboard/${quizId}/recent`);
        if (response.ok) {
            return (yield response.json());
        }
        else {
            log.error(`The quizzes API did not return any leaderboard details. Response.ok: ${response.ok} | Response.status: ${response.status}`);
        }
    });
}
const MAX_CATEGORIES_LENGTH = 1000;
export function findQuizzesInService(communityId, url, categories) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        if (!communityId || !url) {
            log.error('Missing communityId or url params');
            return [];
        }
        const categoriesString = encodeURIComponent(categories
            .filter(Boolean)
            .map((category) => category.toLowerCase().trim())
            .sort()
            .join(',')
            .slice(0, MAX_CATEGORIES_LENGTH));
        const urlString = encodeURIComponent(url);
        const serviceUrl = `quizzes/targeting/find?siteId=${communityId}&url=${urlString}&categories=${categoriesString}`;
        try {
            const response = yield callFandomService(serviceUrl);
            if (response.ok) {
                const paylaod = (yield response.json());
                return (_a = paylaod === null || paylaod === void 0 ? void 0 : paylaod.quizzes) !== null && _a !== void 0 ? _a : [];
            }
            else {
                log.error(`The quizzes API did not return data. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error calling quiz search API', error);
        }
        return [];
    });
}
export function findQuizList(siteId) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const serviceUrl = `quizzes/targeting/find?siteId=${siteId}`;
        try {
            const response = yield callFandomService(serviceUrl);
            if (response.ok) {
                const paylaod = (yield response.json());
                return (_a = paylaod === null || paylaod === void 0 ? void 0 : paylaod.quizzes) !== null && _a !== void 0 ? _a : [];
            }
            else {
                log.error(`The quizzes API did not return data. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error calling quiz search API', error);
        }
        return [];
    });
}
export function getQuizTargetingInfo(quizId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!quizId) {
            log.error('Missing quizId url params');
            return null;
        }
        try {
            const response = yield callFandomService(`quizzes/targeting/${quizId}?cb=${Date.now()}`);
            if (response.ok && response.status !== 404) {
                const payload = (yield response.json());
                return payload !== null && payload !== void 0 ? payload : {};
            }
            else {
                log.error(`The quiz targeting API did not return any targeting data. Response.ok: ${response.ok} | Response: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error calling quiz targeting API', error);
        }
        return null;
    });
}
export function updateQuizTargeting(quizId, siteIds, urls, categories) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!(siteIds === null || siteIds === void 0 ? void 0 : siteIds.length)) {
            throw new Error('Need at least one site ID');
        }
        if (!(categories === null || categories === void 0 ? void 0 : categories.length) && !(urls === null || urls === void 0 ? void 0 : urls.length)) {
            throw new Error('Missing categories or site URL in params');
        }
        const quizTargeting = {
            siteIds: siteIds.filter(Boolean).map((siteId) => parseInt(siteId, 10)),
            urls: urls.filter(Boolean),
            categories: categories.filter(Boolean),
        };
        try {
            const response = yield callFandomService(`quizzes/targeting/${quizId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(quizTargeting),
            });
            if (response.ok && response.status !== 404) {
                const payload = (yield response.json());
                return payload !== null && payload !== void 0 ? payload : { quizzes: [] };
            }
            else {
                log.error(`The quiz targeting submission was not completed. Response.ok: ${response.ok} | Response: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error in submissing the quiz targeting info', error);
        }
        throw new Error('Error in submitting the quiz targeting info');
    });
}
export function createQuiz(quiz) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield callFandomServiceWithBody('quizzes/quizzes', Object.assign({}, quiz));
            if (response.ok) {
                return true;
            }
            else {
                log.error(`The quizzes API did could not create quiz. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error calling quiz post API', error);
        }
        return false;
    });
}
export function uploadQuizImage(formData) {
    return __awaiter(this, void 0, void 0, function* () {
        const { current } = window.SilverSurferSDK.getContext();
        if (![Product.DISCUSSIONS, Product.MEDIAWIKI, Product.TRIVIA, Product.FANHUB].includes(current.product)) {
            throw new Error(`Unsupported product: ${current.product}`);
        }
        try {
            const response = yield callFandomServiceWithFormData(`quizzes/${current.productId}/images`, formData);
            // opaque response won't be OK
            if (response.body) {
                const paylaod = (yield response.json());
                return paylaod === null || paylaod === void 0 ? void 0 : paylaod.url;
            }
            else {
                log.error(`The quizzes API did not return proper data. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error calling quiz upload image API', error);
        }
    });
}
export function getQuizFromDiscussionId(discussionId) {
    return __awaiter(this, void 0, void 0, function* () {
        const { current } = window.SilverSurferSDK.getContext();
        if (![Product.DISCUSSIONS, Product.MEDIAWIKI, Product.TRIVIA].includes(current.product)) {
            throw new Error(`Unsupported product: ${current.product}`);
        }
        if (!discussionId) {
            log.error('Missing discussionId url params');
            return null;
        }
        const response = yield callFandomService(`quizzes/quizzes/discussion/${discussionId}?siteId=${current.productId}`);
        if (!response.ok || response.status === 404) {
            log.debug(`The quizzes discussion API did not return any consumable data. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            return null;
        }
        return (yield response.json());
    });
}
export function getFanHubQuiz(discussionId, communityId) {
    return __awaiter(this, void 0, void 0, function* () {
        const { current } = window.SilverSurferSDK.getContext();
        if (![Product.FANHUB].includes(current.product)) {
            throw new Error(`Unsupported product: ${current.product}`);
        }
        if (!discussionId || !communityId) {
            log.error('Missing discussionId or communityId');
            return null;
        }
        const isDev = isDevDomain() || isLocalHost();
        const response = yield callFandomService(isDev
            ? `quizzes/quizzes/discussion/${discussionId}`
            : `quizzes/quizzes/discussion/${discussionId}?siteId=${communityId}`);
        if (!response.ok || response.status === 404) {
            log.debug(`The quizzes discussion API did not return any consumable data. Response.ok: ${response.ok} | Response.status: ${response.status}`);
            return null;
        }
        return (yield response.json());
    });
}
export function getQuizMetadataInfo(quizId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!quizId) {
            log.error('Missing quizId url params');
            return null;
        }
        try {
            const response = yield callFandomService(`quizzes/quizzes/metadata/${quizId}?cb=${Date.now()}`);
            if (response.ok && response.status !== 404) {
                const payload = (yield response.json());
                return payload !== null && payload !== void 0 ? payload : {};
            }
            else {
                log.error(`The quiz targeting API did not return any metadata. Response.ok: ${response.ok} | Response: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error calling quiz metadata API', error);
        }
        return null;
    });
}
export function updateQuizMetadata(quizId, metadata) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield callFandomService(`quizzes/quizzes/metadata/${quizId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(metadata),
            });
            if (response.ok && response.status !== 404) {
                const payload = (yield response.json());
                return payload !== null && payload !== void 0 ? payload : {};
            }
            else {
                log.error(`The quiz metadata submission was not completed. Response.ok: ${response.ok} | Response: ${response.status}`);
            }
        }
        catch (error) {
            log.error('Error in submissing the quiz metadata', error);
        }
        throw new Error('Error in submitting the quiz metadata');
    });
}
